import classNames from 'classnames'
import { compact } from 'lodash'
import { useLayoutEffect, useRef } from 'react'
import { Conversation } from './Conversation'
import { ConversationListSkeleton } from './ConversationListSkeleton'
import type { ConversationType } from './InboxSubnavs'
import type { ActivityContact } from 'app/types/entities'
import type { MessageJson } from 'app/types/entities'
import { optOutFor } from 'app/lib/smsOptOut'
import { conversationTextMessage, getConversationPhone } from 'app/lib/inboxConversations'

// Smallest row movement worth animating, in pixels. Rows are ~70px tall, so
// anything smaller is reflow noise (an unread badge clearing nudges rows ~3px).
const FLIP_MIN_DELTA_PX = 8

type ConversationListProps = {
  sortedConversations: ConversationType[]
  selectedThreadId: string
  selectedContactId?: string
  names: Record<string, string>
  threadNameOverrides?: Record<string, string>
  isCompact?: boolean
  relatedContacts?: Record<string, ActivityContact[]>
  isLoadingConversations?: boolean
  seenConversations: Record<string, string>
  disabledThreadIds?: Set<string>
  optOuts: ReadonlyMap<string, string>
  /** How many held messages are waiting to send on each thread, keyed by thread
   *  id. Adds a quiet clock next to the row's timestamp; never touches the
   *  preview text, the timestamp value, the sort, or the unread badge (see
   *  `Conversation`'s `scheduledCount` prop). */
  scheduledCountsByThreadId?: Record<string, number>
  onSelectConversation: (conversation: ConversationType) => Promise<void>
  onArchive: (message: MessageJson) => void
  onMarkUnread: (message: MessageJson) => void
}

/**
 * The conversation rows plus the FLIP animation that slides them on reorder.
 *
 * The FLIP measurement must live in the component that renders the rows: the
 * subnav JSX reaches the sidebar through SubnavContext one commit after the
 * page builds it, so an effect anywhere else misses the commit that actually
 * reorders the DOM and the reorder paints unanimated.
 */
const NO_SCHEDULED_COUNTS: Record<string, number> = {}

export function ConversationList({
  sortedConversations,
  selectedThreadId,
  selectedContactId,
  names,
  threadNameOverrides,
  isCompact,
  relatedContacts,
  isLoadingConversations,
  seenConversations,
  disabledThreadIds,
  optOuts,
  scheduledCountsByThreadId = NO_SCHEDULED_COUNTS,
  onSelectConversation,
  onArchive,
  onMarkUnread,
}: ConversationListProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const flipPositionsRef = useRef<Map<string, number>>(new Map())

  // Runs every commit on purpose: rows reorder for many reasons (sync, sort
  // change, pin release) and each lands as a commit of this component. Reads
  // offsetTop rather than getBoundingClientRect().top, which is
  // viewport-relative and would turn scrolling into movement. Costs ~0.5ms at
  // 100 rows; gate on list identity if a profile ever disagrees.
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    const previous = flipPositionsRef.current
    const rows = Array.from(container.querySelectorAll<HTMLElement>(`[data-flip-key]`))
    // Read every position before animating: animate() can force layout, and a
    // mid-loop flush would measure rows against a half-updated layout.
    const measured = rows.map((el) => ({ el, key: el.getAttribute(`data-flip-key`)!, top: el.offsetTop }))
    flipPositionsRef.current = new Map(measured.map(({ key, top }) => [key, top]))
    // First paint has nothing to compare against, so don't animate the list in.
    if (previous.size === 0) return
    for (const { el, key, top } of measured) {
      const previousTop = previous.get(key)
      if (previousTop === undefined) {
        el.animate(
          [
            { opacity: 0, transform: `translateY(-12px)` },
            { opacity: 1, transform: `translateY(0)` },
          ],
          { duration: 300, easing: `ease-out` },
        )
        continue
      }
      const delta = previousTop - top
      if (Math.abs(delta) < FLIP_MIN_DELTA_PX) continue
      el.animate([{ transform: `translateY(${delta}px)` }, { transform: `translateY(0)` }], {
        duration: 300,
        easing: `ease-out`,
      })
    }
  })

  return (
    <div
      ref={containerRef}
      className={classNames(
        `flex min-h-0 grow flex-col overflow-y-auto`,
        isCompact ? `max-w-[80px]` : `max-w-full md:max-w-[380px]`,
      )}
    >
      {isLoadingConversations && sortedConversations.length === 0 ? (
        <ConversationListSkeleton compact={isCompact} />
      ) : (
        compact(sortedConversations).map((conversation, index) => {
          // When a threadId is selected, only the row with that exact threadId
          // is active. Falling back to contactId here would highlight every row
          // sharing the same contact (e.g. duplicate cards for one SMS thread).
          // The contactId fallback only applies when no threadId is selected.
          let isActive = selectedThreadId
            ? conversation.threadId === selectedThreadId
            : Boolean(conversation.contactId && selectedContactId && conversation.contactId === selectedContactId)
          const lastSeenVersion = conversation.threadId ? seenConversations[conversation.threadId] : undefined
          let hasUnseenUpdates = true
          if (conversation.threadId && lastSeenVersion) {
            hasUnseenUpdates = lastSeenVersion !== conversation.id
          }
          const externalPhone = getConversationPhone(conversation)
          const relatedForConversation = relatedContacts?.[externalPhone!]
          const threadNameOverride = conversation.threadId ? threadNameOverrides?.[conversation.threadId] : undefined
          const backendName = names[conversation.contactId!]
          const cleanBackendName = backendName && backendName.toLowerCase() !== `unknown` ? backendName : undefined
          let displayName: string | undefined = threadNameOverride ?? cleanBackendName
          let primaryContactPhone: string | undefined

          // Always use the contact's primary phone for consistency with the thread view
          if (relatedForConversation && relatedForConversation.length > 0) {
            // Use the first contact's phone as the primary
            primaryContactPhone = relatedForConversation[0].phone
          }

          if (!threadNameOverride && relatedContacts && relatedForConversation && relatedForConversation.length > 1) {
            displayName = undefined
          }
          const flipKey = conversation.threadId || conversation.contactId || `conv-${index}`
          return (
            <div key={flipKey} data-flip-key={flipKey} className="w-full">
              <Conversation
                message={conversation}
                name={displayName}
                contactPhone={primaryContactPhone}
                isActive={isActive}
                compact={isCompact}
                hasUnseenUpdates={hasUnseenUpdates}
                relatedContacts={compact(relatedForConversation?.map((rc) => rc.name))}
                agentDisabled={conversation.threadId ? disabledThreadIds?.has(conversation.threadId) : false}
                optedOut={!!optOutFor(conversationTextMessage(conversation), optOuts)}
                scheduledCount={conversation.threadId ? (scheduledCountsByThreadId[conversation.threadId] ?? 0) : 0}
                onClick={async () => {
                  // Re-selecting the active row would wipe its loaded
                  // messages and flash the thread skeleton.
                  if (!isActive) await onSelectConversation(conversation)
                }}
                onArchive={onArchive}
                onMarkUnread={onMarkUnread}
              />
            </div>
          )
        })
      )}
    </div>
  )
}
